import { ApolloError } from '@apollo/client';
import {
  ChartColorContext,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  ECHARTS_CONTAINER_HEIGHT as height,
  nowMarkLineSeriesOption,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, isDefined } from '@e2m-customer-portal/shared';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { DeviceTimeSeries, FeedIn, Interval, MarketLocationType } from 'generated/graphql';
import { ReactElement, useContext, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor } from './utils';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';

function indexOfMSCONSCode(code: string) {
  switch (code) {
    case '220':
      return 1; //actual
    case 'Z18':
      return 2; //temporary
    case '67':
      return 3; //replacement
    default:
      return 0; //missing
  }
}

function powerFeed(
  feedInData: Map<MarketLocationType, FeedIn | undefined>,
  unit: string = 'kW',
): Array<[number, number | null | undefined, number | null | undefined, number | null | undefined]> {
  const producerValues = [...(feedInData.get(MarketLocationType.PRODUCER)?.values ?? [])].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  const consumerValues = [...(feedInData.get(MarketLocationType.CONSUMER)?.values ?? [])].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );

  return producerValues.map((value) => {
    const producerMeasuredValue = convertMeasure({ value: value.measuredValue, unit: 'MW' }, unit).value;
    const consumerMeasuredValue = convertMeasure(
      {
        value: consumerValues.find((consumerValue) => consumerValue.date.getTime() === value.date.getTime())
          ?.measuredValue,
        unit: 'MW',
      },
      unit,
    ).value;
    const combinedMeasuredValue =
      producerMeasuredValue == null || consumerMeasuredValue == null
        ? null
        : producerMeasuredValue + consumerMeasuredValue;

    return [value.date.getTime(), combinedMeasuredValue, producerMeasuredValue, consumerMeasuredValue];
  });
}

function powerFeedQuality(
  feedInData: Map<MarketLocationType, FeedIn | undefined>,
  type: MarketLocationType,
  value?: number,
): [number, number | null, number][] {
  const data = [...(feedInData.get(type)?.values ?? [])].sort((a, b) => a.date.getTime() - b.date.getTime());
  return data.map((item) => {
    const measuredValueQuality = item.msconsCode == null ? null : (value ?? -1);
    const measuredColorQuality = item.msconsCode == null ? 0 : indexOfMSCONSCode(item.msconsCode);

    return [item.date.getTime(), measuredValueQuality, measuredColorQuality];
  });
}

function getDataQualitySegments(data: [number, number | null, number][], endTs = Date.now()) {
  const segments: { start: number; end: number; value: number | null; color: number }[] = [];
  for (let i = 0; i < data.length; i++) {
    const [time, value, color] = data[i];
    const end = i + 1 < data.length ? data[i + 1][0] : endTs;
    const prev = segments.at(-1);
    if (prev?.color === color && prev.end === time) prev.end = end;
    else segments.push({ start: time, end, value, color });
  }
  return segments;
}

interface GridConnectionChartProps {
  dataInterval: Interval;
  deviceTimeSeries?: DeviceTimeSeries;
  deviceTimeSeriesError?: ApolloError;
  feedIn: Map<MarketLocationType, FeedIn | undefined>;
  feedInError?: ApolloError;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function GridConnectionChart({
  dataInterval,
  deviceTimeSeries,
  deviceTimeSeriesError,
  feedIn,
  feedInError,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<GridConnectionChartProps>): ReactElement<GridConnectionChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartColorVariant = useContext(ChartColorContext);
  const chartRef = useRef<EChartsReactRef>(null);
  const actualPowerData = deviceTimeSeries?.datapoints.map((datapoint) => [
    datapoint.date.getTime(),
    convertMeasure({ value: datapoint.actualPower, unit: 'MW' }, 'kW').value,
  ]);

  const MSCONS_COLORS = [
    '#FF0A11',
    ChartColor.Tertiary60[chartColorVariant],
    ChartColor.Secondary60[chartColorVariant],
    ChartColor.Primary60[chartColorVariant],
  ];

  const feedData = powerFeed(feedIn);
  const producerQualityData = powerFeedQuality(feedIn, MarketLocationType.PRODUCER);
  const producerQualityDataSegments = getDataQualitySegments(producerQualityData, producerQualityData.at(-1)?.[0]);
  const consumerQualityData = powerFeedQuality(feedIn, MarketLocationType.CONSUMER, -0.5);
  const consumerQualityDataSegments = getDataQualitySegments(consumerQualityData, consumerQualityData.at(-1)?.[0]);
  const hasTotalMSCONSData = feedData.some(([timestamp, total]) => total != null);
  const hasProducerMSCONSData = feedData.some(([timestamp, total, producer]) => producer != null);
  const hasConsumerMSCONSData = feedData.some(([timestamp, total, producer, consumer]) => consumer != null);

  const feedSeries = {
    data: feedData,
    type: 'line',
    step: 'end',
    symbol: 'circle',
    showSymbol: false,
    tooltip: {
      valueFormatter: (value) => (value == null ? '-' : `${Math.round(Number(value)).toLocaleString(locale)} kW`),
    },
  } satisfies SeriesOption;

  const qualitySeries = {
    type: 'line',
    dimensions: [
      { name: 'time', type: 'time' },
      { name: 'value', type: 'float' },
      { name: 'status', type: 'ordinal' },
    ],
    step: 'end',
    symbol: 'circle',
    showSymbol: false,
    tooltip: {
      valueFormatter: function (value) {
        switch (value) {
          case 1:
            return t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_220', { code: '220' });
          case 2:
            return t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_Z18', { code: 'Z18' });
          case 3:
            return t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_67', { code: '67' });
          default:
            return t('MARKET_LOCATION_FEED_IN_MSCONS_CODE_OTHER');
        }
      },
    },
    connectNulls: false,
    lineStyle: {
      width: 5,
      cap: 'butt',
      join: 'round',
    },
    encode: { x: 0, y: 1, tooltip: [2] },
    color: ChartColor.PrimaryFull[chartColorVariant],
  } satisfies SeriesOption;

  const option: EChartsOption = {
    ...defaultEChartsOptionForInterval(t, dataInterval),
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value} kW',
          color: '#888888',
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
        boundaryGap: ['0.1%', '0.1%'],
      },
      {
        type: 'value',
        min: -1,
        max: 15,
        show: false,
        id: 'quality',
      },
    ],
    visualMap: [
      {
        show: false,
        type: 'piecewise',
        seriesId: 'producer-quality',
        dimension: 0,
        pieces: producerQualityDataSegments.map((s) => ({
          gte: s.start,
          lt: s.end,
          color: MSCONS_COLORS[s.color],
        })),
      },
      {
        show: false,
        type: 'piecewise',
        seriesId: 'consumer-quality',
        dimension: 0,
        pieces: consumerQualityDataSegments.map((s) => ({
          gte: s.start,
          lt: s.end,
          color: MSCONS_COLORS[s.color],
        })),
      },
    ],
    series: [
      deviceTimeSeries
        ? {
            ...feedSeries,
            name: t('MARKET_LOCATION_FEED_IN_CHART_MEASURED_VPP'),
            data: actualPowerData,
            color: ChartColor.SecondaryFull[chartColorVariant],
          }
        : undefined,
      hasTotalMSCONSData
        ? {
            ...feedSeries,
            name: t('MARKET_LOCATION_FEED_IN_CHART_MEASURED_TOTAL'),
            color: ChartColor.PrimaryFull[chartColorVariant],
            z: 10,
          }
        : undefined,
      hasProducerMSCONSData
        ? [
            {
              ...feedSeries,
              name: t('MARKET_LOCATION_FEED_IN_CHART_MEASURED_DISCHARGE'),
              color: ChartColor.TertiaryFull[chartColorVariant],
              encode: { y: 2 },
            },
            {
              ...qualitySeries,
              name: t('COLOCATION_OVERVIEW_CHARTS_MSCONS_PRODUCER'),
              data: producerQualityData,
              id: 'producer-quality',
              yAxisId: 'quality',
            },
          ]
        : undefined,
      hasConsumerMSCONSData
        ? [
            {
              ...feedSeries,
              name: t('MARKET_LOCATION_FEED_IN_CHART_MEASURED_CHARGE'),
              color: ChartColor.Tertiary60[chartColorVariant],
              encode: { y: 3 },
            },
            {
              ...qualitySeries,
              name: t('COLOCATION_OVERVIEW_CHARTS_MSCONS_CONSUMER'),
              data: consumerQualityData,
              id: 'consumer-quality',
              yAxisId: 'quality',
            },
          ]
        : undefined,
      nowMarkLineSeriesOption,
    ]
      .flat()
      .filter(isDefined),
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-40"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          color="inherit"
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="grid-connection-chart"
        onChartReady={onChartReadyCallback}
        ref={chartRef}
      />
      {(feedInError || deviceTimeSeriesError) && <ChartErrorComponent errors={[feedInError, deviceTimeSeriesError]} />}
    </div>
  );
}
